import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import cn from 'classnames';
import Slider from 'components/UI/Slider';
import { CustomDot, CustomPagination } from 'components/UI/Slider/utils';
import LazyImage from 'components/UI/LazyImage';
import FullscreenButton from 'components/UI/FullscreenButton';
import { useStateModal, useWindowResize } from 'utils/hooks';

import * as styles from './slider.module.scss';

const ImageModal = lazy(() => import('components/Modals/ImageModal'));

export function Component({ content, className }) {
  const { modalState, showModal, hideModal } = useStateModal();
  const [current, setCurrent] = useState(0);
  const [currentImg, setCurrentImg] = useState(null);
  const topSlider = useRef();
  const bottomSlider = useRef();
  const { width } = useWindowResize();

  const [desktopSlider, setDesktopSlider] = useState({
    currentPhotoIndex: 1,
    topSliderSettings: {
      arrows: false,
      fade: true,
      swipe: true,
      centerMode: true,
      centerPadding: 0,
      asNavFor: bottomSlider.current,
    },
    bottomSliderSettings: {
      arrows: false,
      asNavFor: topSlider.current,
    },
  });

  useEffect(() => {
    setDesktopSlider(prev => ({
      ...prev,
      topSliderSettings: {
        ...prev.topSliderSettings,
      },
      bottomSliderSettings: {
        ...prev.bottomSliderSettings,
        slidesToShow: content.length > 6 ? 6 : content.length,
      },
    }));
  }, []);

  const { topSliderSettings, bottomSliderSettings } = desktopSlider;

  const setCurrentPhotoIndex = (current, next) => {
    setCurrent(next);
    setDesktopSlider(prev => {
      return {
        ...prev,
        currentPhotoIndex: next + 1,
      };
    });
  };

  const togglePhoto = i => {
    topSlider.current.slickGoTo(i);
    bottomSlider.current.slickGoTo(i);
  };

  const toggleNext = () => {
    topSlider.current.slickNext();
    bottomSlider.current.slickNext();
  };

  const togglePrev = () => {
    topSlider.current.slickPrev();
    bottomSlider.current.slickPrev();
  };

  const mobileSliderSettings = useMemo(
    () => ({
      arrows: false,
      customPaging: CustomDot.bind(null, content.length, current),
      appendDots: CustomPagination,
      dotsClass: 'slick-pagination',
      initialSlide: 0,
      dots: true,
      fade: true,
      slidesToShow: 1,
      slidesToScroll: 1,
    }),
    [content.length, current],
  );

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const callModal = useCallback(img => {
    setCurrentImg(img);
    showModal();
  }, []);

  return (
    <div className={cn(className, styles.wrapper)}>
      {width < 545 ? (
        <div className="defaultSlider">
          <Slider {...mobileSliderSettings} beforeChange={setCurrentKey}>
            {content.map(({ img, figcapture }, i) => (
              <figure key={`Slide-${i}`} className={styles.figure}>
                <LazyImage src={img} alt={figcapture ?? `Slide-${i}`} className={styles.bigImg} />
                <FullscreenButton className={styles.zoomBtn} onClick={() => callModal(img)} />
                {figcapture && <figcaption className={styles.figcaption}>{figcapture}</figcaption>}
              </figure>
            ))}
          </Slider>
        </div>
      ) : (
        <>
          <Slider
            ref={topSlider}
            onSwipe={toggle => (toggle === 'left' ? toggleNext() : togglePrev())}
            beforeChange={setCurrentPhotoIndex}
            {...topSliderSettings}
          >
            {content.map(({ img, figcapture }, i) => (
              <figure key={`Slide-${i}`} className={styles.figure}>
                <LazyImage src={img} alt={figcapture ?? `Slide-${i}`} className={styles.bigImg} />
                <FullscreenButton className={styles.zoomBtn} onClick={() => callModal(img)} />
                {figcapture && <figcaption className={styles.figcaption}>{figcapture}</figcaption>}
              </figure>
            ))}
          </Slider>
          <div className={styles.bottomWrapper}>
            <div className={styles.navigation}>
              <button className={cn(styles.arrow, styles.next)} onClick={toggleNext} />
              <button className={cn(styles.arrow, styles.prev)} onClick={togglePrev} />
            </div>
            <Slider ref={bottomSlider} {...bottomSliderSettings} className={styles.bottomSlider}>
              {content.map(({ img, figcapture }, i) => (
                <LazyImage
                  key={`Slide-${i}`}
                  src={img}
                  alt={figcapture ?? `Slide-${i}`}
                  onClick={() => togglePhoto(i)}
                />
              ))}
            </Slider>
            <div className={styles.amount}>
              {desktopSlider.currentPhotoIndex}/{content.length}
            </div>
          </div>
        </>
      )}
      <Suspense fallback={null}>
        <ImageModal
          isOpen={modalState.isOpen}
          closeModalCallback={hideModal}
          img={{ desktop: currentImg }}
        />
      </Suspense>
    </div>
  );
}
